import { useEffect, useMemo, useState } from "react";
import {
  Calendar as CalendarIcon,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  Lock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
} from "@sportscanner/ui";
import DatePicker from "./date-picker";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import { blockBulkSchema } from "@sportscanner/schemas";
import { apiPost, getApiErrorCode } from "@/lib/api";
import type { MeClub, MeCourt, MeSport } from "@/lib/me";
import {
  dateStrToEntry,
  formatCourtLabel,
  toLocalDateStr,
  addDays as addDaysHelper,
} from "./helpers";
import RecurrenceField, {
  defaultRecurrence,
  type RecurrenceValue,
} from "./recurrence-field";
import { toast } from "sonner";

type BlockConflictReason =
  | "SLOT_TAKEN"
  | "ALREADY_BLOCKED"
  | "BOOKING_IN_PAST"
  | "CLUB_CLOSED";

interface BulkBlockResponse {
  seriesId: string;
  sportId: string;
  sportName: string;
  sessionDurationMinutes: number;
  reason: string;
  created: Array<{
    id: string;
    courtId: string;
    date: string;
    startTime: string;
    endTime: string;
  }>;
  conflicts: Array<{
    courtId: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: BlockConflictReason;
  }>;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  club: MeClub | null;
  /** Prefill if the dialog is launched from a calendar cell. */
  shortcut?: {
    date: string;
    time?: string;
    sportId?: string;
    courtId?: string;
  };
  onCreated?: () => void;
}

function generateGridSlots(
  start: string,
  end: string,
  durationMinutes: number,
): string[] {
  const out: string[] = [];
  const toMin = (s: string) => {
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m;
  };
  const fromMin = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  const startMin = toMin(start);
  const endMin = toMin(end);
  for (let m = startMin; m + durationMinutes <= endMin; m += durationMinutes) {
    out.push(fromMin(m));
  }
  return out;
}

export default function BlockSlotDialog({
  open,
  onOpenChange,
  clubId,
  club,
  shortcut,
  onCreated,
}: Props) {
  const { t } = useTranslation();

  const sports: MeSport[] = useMemo(() => club?.sports ?? [], [club]);
  const courts: MeCourt[] = useMemo(
    () => (club?.courts ?? []).filter((c) => c.isActive),
    [club],
  );

  const [step, setStep] = useState<"form" | "result">("form");
  const [sportId, setSportId] = useState<string>("");
  const [selectedCourtId, setSelectedCourtId] = useState<string>("");
  const [date, setDate] = useState<string>(
    shortcut?.date ?? toLocalDateStr(new Date()),
  );
  const [ranges, setRanges] = useState<Array<{ start: string; end: string }>>([
    { start: shortcut?.time ?? "", end: "" },
  ]);
  const [reason, setReason] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceValue>(() =>
    defaultRecurrence(shortcut?.date ?? toLocalDateStr(new Date())),
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkBlockResponse | null>(null);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setStep("form");
    setSportId(shortcut?.sportId ?? sports[0]?.id ?? "");
    setSelectedCourtId(shortcut?.courtId ?? "");
    setDate(shortcut?.date ?? toLocalDateStr(new Date()));
    setRanges([{ start: shortcut?.time ?? "", end: "" }]);
    setReason("");
    setIsRecurring(false);
    setRecurrence(
      defaultRecurrence(shortcut?.date ?? toLocalDateStr(new Date())),
    );
    setResult(null);
    setIsSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sport = useMemo(
    () => sports.find((s) => s.id === sportId),
    [sports, sportId],
  );

  const sportCourts = useMemo(
    () => (sportId ? courts.filter((c) => c.sportId === sportId) : []),
    [courts, sportId],
  );

  // Default to the first court of the picked sport unless one is already
  // chosen (from the calendar-cell prefill or a previous sport that still
  // resolves to a valid court here).
  useEffect(() => {
    if (!sportId) return;
    if (shortcut?.courtId && courts.some((c) => c.id === shortcut.courtId)) {
      setSelectedCourtId(shortcut.courtId);
      return;
    }
    setSelectedCourtId((current) =>
      sportCourts.some((c) => c.id === current)
        ? current
        : (sportCourts[0]?.id ?? ""),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportId]);

  const selectedCourt = useMemo(
    () => sportCourts.find((c) => c.id === selectedCourtId),
    [sportCourts, selectedCourtId],
  );

  const sessionDuration = sport?.sessionDurationMinutes ?? 60;
  // Scope the slot grid to the selected court's effective hours so a 06:00
  // court doesn't expose hours its neighbour doesn't open until 08:00.
  const courtStart =
    selectedCourt?.workingHoursStart || club?.workingHoursStart || "08:00";
  const courtEnd =
    selectedCourt?.workingHoursEnd || club?.workingHoursEnd || "22:00";

  // Possible start times = grid slots within the selected court's hours.
  const startOptions = useMemo(
    () => generateGridSlots(courtStart, courtEnd, sessionDuration),
    [courtStart, courtEnd, sessionDuration],
  );

  // If the previously picked start no longer fits the new court's grid (e.g.
  // sport or court change), clear the affected ranges so the UI stays valid.
  useEffect(() => {
    setRanges((rs) =>
      rs.map((r) => {
        const startOk = startOptions.includes(r.start);
        return startOk ? r : { start: "", end: "" };
      }),
    );
  }, [startOptions]);

  function endOptionsFor(start: string): string[] {
    if (!start) return [];
    // End is start + N * duration, up to the court's working-hours end.
    const toMin = (s: string) => {
      const [h, m] = s.split(":").map(Number);
      return h * 60 + m;
    };
    const fromMin = (m: number) =>
      `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
    const startMin = toMin(start);
    const endMin = toMin(courtEnd);
    const out: string[] = [];
    for (
      let m = startMin + sessionDuration;
      m <= endMin;
      m += sessionDuration
    ) {
      out.push(fromMin(m));
    }
    return out;
  }

  function updateRange(i: number, key: "start" | "end", value: string) {
    setRanges((rs) => {
      const next = [...rs];
      next[i] = { ...next[i], [key]: value };
      // If start changes, clear end if it's no longer >= start + duration.
      if (key === "start") next[i].end = "";
      return next;
    });
  }

  function addRange() {
    if (ranges.length >= 8) return;
    setRanges((rs) => [...rs, { start: "", end: "" }]);
  }

  function removeRange(i: number) {
    setRanges((rs) =>
      rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i),
    );
  }

  const canSubmit = useMemo(() => {
    if (!sportId || !selectedCourtId) return false;
    if (!reason.trim()) return false;
    if (ranges.some((r) => !r.start || !r.end)) return false;
    if (isRecurring) {
      if (!recurrence.untilDate) return false;
      if (recurrence.frequency === "WEEKLY" && recurrence.weekdays.length === 0)
        return false;
    } else {
      if (!date) return false;
    }
    return !isSubmitting;
  }, [
    sportId,
    selectedCourtId,
    reason,
    ranges,
    isRecurring,
    recurrence,
    date,
    isSubmitting,
  ]);

  async function submit() {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const body = {
        courtIds: [selectedCourtId],
        sportId,
        timeRanges: ranges.map((r) => ({ start: r.start, end: r.end })),
        reason: reason.trim(),
        ...(isRecurring
          ? {
              recurrence:
                recurrence.frequency === "DAILY"
                  ? {
                      frequency: "DAILY",
                      startDate: date,
                      untilDate: recurrence.untilDate,
                    }
                  : {
                      frequency: "WEEKLY",
                      startDate: date,
                      untilDate: recurrence.untilDate,
                      weekdays: recurrence.weekdays,
                    },
            }
          : { date }),
      };
      const parsed = blockBulkSchema.safeParse(body);
      if (!parsed.success) {
        toast.error(
          t(
            `errors.api.${parsed.error.issues[0]?.message ?? "VALIDATION_FAILED"}`,
          ),
        );
        return;
      }
      const data = await apiPost<BulkBlockResponse>(
        `/api/admin/clubs/${clubId}/blocks`,
        parsed.data,
      );
      setResult(data);
      setStep("result");
      onCreated?.();
    } catch (err) {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    } finally {
      setIsSubmitting(false);
    }
  }

  const minDate = toLocalDateStr(new Date());
  const maxDate = toLocalDateStr(addDaysHelper(new Date(), 90));

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="md:max-w-lg md:max-h-[90vh] md:overflow-y-auto">
        {step === "result" && result ? (
          <BlockResultStep
            result={result}
            onMakeAnother={() => {
              setStep("form");
              setReason("");
              setResult(null);
            }}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-red-500" />
                {t("blocks.form.title")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Sport */}
              {sports.length > 1 && (
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-neutral-700">
                    {t("blocks.form.sport")}
                  </Label>
                  <ToggleGroup
                    type="single"
                    value={sportId}
                    onValueChange={(v: string) => v && setSportId(v)}
                    className="w-full gap-2"
                  >
                    {sports.map((s) => (
                      <ToggleGroupItem
                        key={s.id}
                        value={s.id}
                        className="flex-1 py-2 rounded-full border border-neutral-200 text-sm font-semibold cursor-pointer data-[state=off]:bg-white data-[state=off]:text-neutral-600 data-[state=on]:bg-primary-500 data-[state=on]:text-white data-[state=on]:border-primary-500"
                      >
                        {t(`sports.${s.name}`)}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>
              )}

              {/* Courts */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-neutral-700">
                  {t("blocks.form.court")}
                </Label>
                <ToggleGroup
                  type="single"
                  value={selectedCourtId}
                  onValueChange={(v: string) => v && setSelectedCourtId(v)}
                  className="flex flex-wrap gap-2"
                >
                  {sportCourts.map((c) => (
                    <ToggleGroupItem
                      key={c.id}
                      value={c.id}
                      className="px-3 py-1.5 rounded-full border border-neutral-200 text-sm cursor-pointer data-[state=off]:bg-white data-[state=off]:text-neutral-600 data-[state=on]:bg-primary-500 data-[state=on]:text-white data-[state=on]:border-primary-500"
                    >
                      {formatCourtLabel(c.name, t("blocks.form.court"))}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                {sportCourts.length === 0 && (
                  <p className="text-xs text-neutral-400">
                    {t("blocks.form.noCourts")}
                  </p>
                )}
              </div>

              {/* Date */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-neutral-700">
                  {isRecurring
                    ? t("blocks.form.firstDate")
                    : t("blocks.form.date")}
                </Label>
                <DatePicker
                  value={date}
                  onChange={setDate}
                  min={minDate}
                  max={maxDate}
                  disabled={isSubmitting}
                />
              </div>

              {/* Time ranges */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-neutral-700">
                  {t("blocks.form.timeRanges")}
                </Label>
                {ranges.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Select
                      value={r.start}
                      onValueChange={(v) => updateRange(i, "start", v)}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder={t("blocks.form.from")} />
                      </SelectTrigger>
                      <SelectContent>
                        {startOptions.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-neutral-400 text-sm">–</span>
                    <Select
                      value={r.end}
                      onValueChange={(v) => updateRange(i, "end", v)}
                      disabled={isSubmitting || !r.start}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder={t("blocks.form.to")} />
                      </SelectTrigger>
                      <SelectContent>
                        {endOptionsFor(r.start).map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {ranges.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRange(i)}
                        className="text-neutral-400 hover:text-red-500 cursor-pointer"
                        aria-label={t("blocks.form.removeRange")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {ranges.length < 8 && (
                  <button
                    type="button"
                    onClick={addRange}
                    className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t("blocks.form.addRange")}
                  </button>
                )}
              </div>

              {/* Reason */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="block-reason"
                  className="text-sm font-semibold text-neutral-700"
                >
                  {t("blocks.form.reason")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="block-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("blocks.form.reasonPlaceholder")}
                  rows={2}
                  maxLength={200}
                  disabled={isSubmitting}
                />
              </div>

              {/* Recurrence */}
              <RecurrenceField
                enabled={isRecurring}
                onEnabledChange={setIsRecurring}
                value={recurrence}
                onChange={setRecurrence}
                startDate={date}
                disabled={isSubmitting}
              />

              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white py-3 h-auto disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("blocks.form.creating")}
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    {t("blocks.form.submit")}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function BlockResultStep({
  result,
  onMakeAnother,
  onClose,
}: {
  result: BulkBlockResponse;
  onMakeAnother: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { created, conflicts } = result;
  const allFailed = created.length === 0;

  function fmt(date: string) {
    const e = dateStrToEntry(date);
    return `${t(`days.long.${e.dayKey}`)}, ${e.date}. ${t(`months.${e.monthKey}`).slice(0, 3)}`;
  }

  return (
    <div className="space-y-5 py-2">
      <div className="flex justify-center">
        {allFailed ? (
          <div className="bg-red-100 rounded-full p-3">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
        ) : conflicts.length > 0 ? (
          <div className="bg-amber-100 rounded-full p-3">
            <AlertCircle className="w-10 h-10 text-amber-600" />
          </div>
        ) : (
          <CheckCircle2 className="w-14 h-14 text-emerald-500" />
        )}
      </div>

      <div className="text-center space-y-1">
        <h2 className="font-heading text-xl font-bold text-neutral-800">
          {allFailed
            ? t("blocks.result.allFailedTitle")
            : conflicts.length > 0
              ? t("blocks.result.partialTitle")
              : t("blocks.result.successTitle")}
        </h2>
        <p className="text-sm text-neutral-500">
          {t("blocks.result.summary", {
            created: created.length,
            conflicts: conflicts.length,
          })}
        </p>
      </div>

      {created.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1.5 max-h-60 overflow-y-auto">
          <p className="font-heading font-semibold text-sm text-emerald-800 mb-1">
            {t("blocks.result.createdHeading", { count: created.length })}
          </p>
          {created.map((b) => (
            <div
              key={b.id}
              className="flex items-center justify-between text-sm text-neutral-700"
            >
              <span className="flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5 text-emerald-600" />
                {fmt(b.date)}
              </span>
              <span className="text-neutral-500">
                {b.startTime}–{b.endTime}
              </span>
            </div>
          ))}
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5 max-h-60 overflow-y-auto">
          <p className="font-heading font-semibold text-sm text-amber-800 mb-1">
            {t("blocks.result.conflictsHeading", { count: conflicts.length })}
          </p>
          {conflicts.map((c, i) => (
            <div
              key={`${c.date}-${c.startTime}-${c.courtId}-${i}`}
              className="flex items-center justify-between gap-2 text-sm text-neutral-700"
            >
              <span>{fmt(c.date)}</span>
              <span className="text-xs text-neutral-500">
                {c.startTime} · {t(`blocks.result.reason.${c.reason}`)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onMakeAnother}
          className="flex-1 rounded-full"
        >
          {t("blocks.form.newOne")}
        </Button>
        <Button
          onClick={onClose}
          className="flex-1 rounded-full bg-primary-600 hover:bg-primary-700 text-white"
        >
          {t("blocks.form.close")}
        </Button>
      </div>
    </div>
  );
}
